import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { useTypedSelector } from '../../../hooks/useTypedSelector';
import { CacheKey, StopType } from '../../../types';
import { parseLine1Line2 } from '../../../utils/address';
import { coreClient } from '../../../utils/request';
import { actions, showDutchAddresLayout } from '../ducks';
import { AddressCreateParams, GeoAutocompleteAddress } from '@brenger/api-client';

interface UseStopContactAddressPrefill {
  isLoading: boolean;
}

/**
 * Hook that takes care of prefilling the correct info
 */

export const useStopContactAddressPrefill = (stopType: 'pickup' | 'delivery'): UseStopContactAddressPrefill => {
  const destination = useTypedSelector(state => state.generalTransport.destination);
  const accountSlug = destination[stopType === 'pickup' ? 'pickup_meta' : 'delivery_meta']?.utm_account;
  const existingData = useTypedSelector(state => state.generalTransport.contact[stopType]?.address);
  const isDutchLayout = useTypedSelector(showDutchAddresLayout);
  const dispatch = useDispatch();

  const publicAccount = useQuery({
    queryKey: [CacheKey.RETRIEVE_PUBLIC_DATA, stopType, accountSlug],
    queryFn: () => coreClient.accounts.retrievePublicDataBySlug({ slug: accountSlug as string }),
    enabled: !!accountSlug,
  });

  React.useEffect(() => {
    const account = publicAccount.data?.account;
    if (!existingData && typeof account === 'object' && account?.address) {
      dispatch(
        actions.setStopAddress({
          stopType: stopType === 'pickup' ? StopType.PICKUP : StopType.DELIVERY,
          address: parseLine1Line2(account.address, isDutchLayout),
          address_is_prefilled: true,
        })
      );
      return;
    }
    // When no account data is foud we should check how complete the destination address is
    const wa = destination[stopType];
    if (!wa) return;

    const { latitude, longitude, ...rest } = parseLine1Line2(
      wa as AddressCreateParams,
      isDutchLayout
    ) as GeoAutocompleteAddress;
    const address = {
      ...rest,
      lat: latitude,
      lng: longitude,
    } as AddressCreateParams;
    if (
      address.line1 &&
      (isDutchLayout ? address.line2 : true) &&
      address.postal_code &&
      address.locality &&
      address.lat &&
      address.lng
    ) {
      dispatch(
        actions.setStopAddress({
          stopType: stopType === 'pickup' ? StopType.PICKUP : StopType.DELIVERY,
          address,
          address_is_prefilled: true,
        })
      );
    }

    // TODO: handle full widget case
  }, [publicAccount.data]);

  return {
    isLoading: publicAccount.isLoading,
  };
};
